import React, { useMemo, useState } from 'react';
import { Drawer, Alert, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Clock, Lightbulb, ArrowRightLeft, Trophy, GraduationCap, CalendarDays, Briefcase, Building2, Hourglass, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import UserSidebar, { USER_PAGE_ROW } from '../../components/SidebarNew/UserSidebar';
import Layout from '../../Layout';
import { currentUser } from '../Social/socialStore';
import { UserAvatar } from '../Social/component/UserAvatar';
import { BirthdayWidget } from '../Social/rightPanel/BirthdayWidget';
import { TYPE_META, WORK_RECORDS, currentProfile, recordStatus, tenure, type RecordType, type WorkRecord } from './workData';

const TYPE_ICON: Record<RecordType, typeof Clock> = { contract: Clock, position: Lightbulb, transfer: ArrowRightLeft, reward: Trophy, training: GraduationCap };
const TONE: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300',
  ended: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300',
  done: 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300',
  upcoming: 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
};

function Stat({ icon: Icon, label, value }: { icon: typeof Clock; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <span className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4" /></span>
      <span className="min-w-0">
        <span className="block text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span>
        <span className="block text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{value}</span>
      </span>
    </div>
  );
}

export default function WorkProcessPage() {
  const me = useMemo(currentUser, []);
  const navigate = useNavigate();
  const [filter, setFilter] = useState<RecordType | 'all'>('all');
  const [open, setOpen] = useState<WorkRecord | null>(null);
  const now = dayjs();
  const profile = currentProfile(WORK_RECORDS, now);

  const counts = useMemo(() => {
    const c: Partial<Record<RecordType, number>> = {};
    WORK_RECORDS.forEach(r => { c[r.type] = (c[r.type] || 0) + 1; });
    return c;
  }, []);

  // mới nhất lên đầu, gom theo năm
  const groups = useMemo(() => {
    const list = WORK_RECORDS.filter(r => filter === 'all' || r.type === filter).sort((a, b) => b.date.localeCompare(a.date));
    const map = new Map<string, WorkRecord[]>();
    list.forEach(r => { const y = r.date.slice(0, 4); map.set(y, [...(map.get(y) || []), r]); });
    return Array.from(map.entries());
  }, [filter]);

  const days = profile.contractDaysLeft;

  return (
    <Layout>
      <Helmet><title>Quá trình làm việc | Khothi</title></Helmet>
      <div className={USER_PAGE_ROW}>
        <UserSidebar />

        <div className="w-full min-w-0 grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
          <div className="xl:col-span-2 space-y-4 min-w-0">
            {/* Tổng quan hồ sơ */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
              <div className="flex items-center gap-4 mb-4">
                <UserAvatar name={me.name} src={me.avatar} size={52} />
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{me.name}</h1>
                  <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{profile.position || 'Chưa có chức vụ'}{profile.unit ? ` · ${profile.unit.split(' › ').slice(-1)[0]}` : ''}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat icon={CalendarDays} label="Ngày vào làm" value={profile.joinDate ? dayjs(profile.joinDate).format('DD/MM/YYYY') : '—'} />
                <Stat icon={Hourglass} label="Thâm niên" value={profile.joinDate ? tenure(profile.joinDate, now) : '—'} />
                <Stat icon={Briefcase} label="Chức vụ hiện tại" value={profile.position || '—'} />
                <Stat icon={Building2} label="Hợp đồng" value={profile.contract ? (profile.contract.endDate ? `đến ${dayjs(profile.contract.endDate).format('DD/MM/YYYY')}` : 'Không thời hạn') : 'Chưa có'} />
              </div>
              {days !== null && days <= 60 && (
                <Alert
                  className="!mt-4"
                  type={days <= 15 ? 'error' : 'warning'}
                  showIcon
                  message={days < 0 ? 'Hợp đồng đã hết hạn' : `Hợp đồng còn ${days} ngày là hết hạn (${dayjs(profile.contract!.endDate).format('DD/MM/YYYY')})`}
                  description="Liên hệ phòng Nhân sự để gia hạn hoặc ký hợp đồng mới."
                />
              )}
            </div>

            {/* Lọc theo loại */}
            <div className="flex flex-wrap gap-2">
              {(['all', ...Object.keys(TYPE_META)] as (RecordType | 'all')[]).filter(k => k === 'all' || counts[k as RecordType]).map(k => {
                const active = filter === k;
                const n = k === 'all' ? WORK_RECORDS.length : counts[k as RecordType];
                return (
                  <button key={k} type="button" onClick={() => setFilter(k)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition cursor-pointer ${active ? 'bg-[#3e91ab] border-[#3e91ab] text-white' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300'}`}>
                    {k === 'all' ? 'Tất cả' : TYPE_META[k as RecordType].label} <span className={active ? 'opacity-80' : 'text-slate-400'}>{n}</span>
                  </button>
                );
              })}
            </div>

            {/* Dòng thời gian */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 md:p-6">
              {groups.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có mốc nào" />}
              {groups.map(([year, items]) => (
                <div key={year} className="mb-6 last:mb-0">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Năm {year}</div>
                  <div className="space-y-4">
                    {items.map((item, index) => {
                      const Icon = TYPE_ICON[item.type];
                      const meta = TYPE_META[item.type];
                      const st = recordStatus(item, WORK_RECORDS, now);
                      return (
                        <div key={item.id} className="flex items-start gap-4 sm:gap-6 relative">
                          {index !== items.length - 1 && <div className="absolute left-[31px] sm:left-[39px] top-[52px] bottom-[-20px] w-[2px] bg-slate-100 dark:bg-slate-700" />}
                          <div className="flex flex-col items-center shrink-0 w-16 sm:w-20 text-center">
                            <span className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200 leading-tight mb-2">{dayjs(item.date).format('DD.MM')}</span>
                            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center ${meta.bg} ${meta.color}`}>
                              <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                          </div>
                          <button type="button" onClick={() => setOpen(item)}
                            className="flex-1 min-w-0 text-left bg-slate-50/70 dark:bg-slate-900/50 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-4 hover:border-[#3e91ab] hover:shadow-sm transition cursor-pointer group">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                              <h4 className="font-bold text-slate-800 dark:text-slate-100 text-sm sm:text-base group-hover:text-[#3e91ab]">{item.title}</h4>
                              <span className={`self-start sm:self-auto px-2.5 py-0.5 text-xs font-semibold rounded-md whitespace-nowrap ${TONE[st.tone]}`}>{st.label}</span>
                            </div>
                            <div className="text-xs text-slate-400 font-medium">
                              {meta.label}{item.code ? ` · ${item.code}` : ''}{item.endDate ? ` · ${dayjs(item.date).format('DD/MM/YYYY')} – ${dayjs(item.endDate).format('DD/MM/YYYY')}` : ''}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-2">
                              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide truncate">{item.unit}</span>
                              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#3e91ab] flex-shrink-0" />
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cột phải: sinh nhật & kỷ niệm (bấm Chúc mừng → mở bài chúc ở Bảng tin) */}
          <BirthdayWidget meId={me.id} onWish={o => navigate(`/social?wish=${o.person.id}:${o.kind}`)} />
        </div>
      </div>

      {/* Chi tiết một mốc */}
      <Drawer open={!!open} onClose={() => setOpen(null)} width={420} title={open ? TYPE_META[open.type].label : ''} destroyOnHidden>
        {open && (() => {
          const st = recordStatus(open, WORK_RECORDS, now);
          const rows = [
            { label: 'Ngày hiệu lực', value: dayjs(open.date).format('DD/MM/YYYY') },
            ...(open.endDate ? [{ label: 'Ngày kết thúc', value: dayjs(open.endDate).format('DD/MM/YYYY') }] : []),
            ...(open.code ? [{ label: 'Số hiệu', value: open.code }] : []),
            { label: 'Đơn vị', value: open.unit },
            ...(open.details || []),
          ];
          return (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{open.title}</h3>
                <span className={`inline-block mt-1 px-2.5 py-0.5 text-xs font-semibold rounded-md ${TONE[st.tone]}`}>{st.label}</span>
              </div>
              <dl className="divide-y divide-slate-100 dark:divide-slate-700 border border-slate-100 dark:border-slate-700 rounded-xl">
                {rows.map(r => (
                  <div key={r.label} className="flex gap-3 px-4 py-2.5 text-sm">
                    <dt className="w-32 flex-shrink-0 text-slate-500">{r.label}</dt>
                    <dd className="font-medium text-slate-800 dark:text-slate-100 min-w-0 break-words">{r.value}</dd>
                  </div>
                ))}
              </dl>
              {open.note && <p className="text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 rounded-xl p-3">{open.note}</p>}
              <p className="text-xs text-slate-400">Thông tin sai lệch? Vui lòng liên hệ phòng Nhân sự để được cập nhật.</p>
            </div>
          );
        })()}
      </Drawer>
    </Layout>
  );
}
